import React, { useEffect, useState } from "react";
import api from "../api";
import { BookOpen, Calendar } from "lucide-react";

export default function CarnetClient() {
  const [data, setData] = useState({ notes: [], mns: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/carnet")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center text-slate-500 py-12">Chargement…</div>;

  if (data.notes.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="carnet-empty">
        <h1 className="text-3xl font-bold text-slate-900 mb-2 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <BookOpen className="w-7 h-7 text-sky-700" />
          Carnet de progression
        </h1>
        <div className="card-soft p-8 text-center text-slate-500">
          Votre carnet est vide pour l'instant. Vos coachs y partageront leurs observations pédagogiques au fil des cours.
        </div>
      </div>
    );
  }

  // Group notes by month for a timeline-feel
  const groups = {};
  for (const n of data.notes) {
    const key = (n.lesson_date || "").slice(0, 7); // YYYY-MM
    (groups[key] = groups[key] || []).push(n);
  }
  const orderedKeys = Object.keys(groups).sort().reverse();

  return (
    <div className="max-w-3xl mx-auto px-4 py-10 space-y-6" data-testid="carnet-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <BookOpen className="w-7 h-7 text-sky-700" />
          Carnet de progression
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Toutes les observations partagées par vos coachs ({data.notes.length} note{data.notes.length > 1 ? "s" : ""}).
        </p>
      </div>

      {orderedKeys.map((monthKey) => {
        const [yyyy, mm] = monthKey.split("-");
        const monthLabel = new Date(parseInt(yyyy, 10), parseInt(mm, 10) - 1, 1)
          .toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
        return (
          <div key={monthKey} data-testid={`carnet-month-${monthKey}`}>
            <h2 className="text-sm uppercase tracking-wider font-semibold text-slate-500 mb-3 flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" />{monthLabel}
            </h2>
            <div className="space-y-3">
              {groups[monthKey].map((n) => {
                const mns = data.mns[n.mns_id];
                const pic = mns?.picture
                  ? (mns.picture.startsWith("http") ? mns.picture : `${process.env.REACT_APP_BACKEND_URL}${mns.picture}`)
                  : `https://ui-avatars.com/api/?name=${encodeURIComponent(mns?.name || "Coach")}&background=0369A1&color=fff`;
                return (
                  <div key={n.note_id} className="card-soft p-4" data-testid={`carnet-note-${n.note_id}`}>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                      <img src={pic} alt="" className="w-6 h-6 rounded-full ring-1 ring-sky-100" />
                      <span><strong className="text-slate-900">{mns?.name || "Coach"}</strong> · leçon du {new Date(n.lesson_date).toLocaleDateString("fr-FR")}</span>
                    </div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</div>
                    {n.photos && n.photos.length > 0 && (
                      <div className="mt-3 flex gap-2 flex-wrap">
                        {n.photos.map((src, i) => (
                          <a key={src} href={`${process.env.REACT_APP_BACKEND_URL}${src}`} target="_blank" rel="noreferrer">
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${src}`}
                              alt=""
                              className="w-24 h-24 rounded-lg object-cover ring-1 ring-slate-200 hover:ring-sky-400 transition"
                              data-testid={`carnet-photo-${n.note_id}-${i}`}
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
